import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  Film,
  Play,
  Download,
  Activity,
  Settings,
  Link,
  LogOut,
  ChevronDown,
  Plus,
  Zap,
  Award,
  ShieldCheck,
} from 'lucide-react';
import { ProgressBar } from '../ProgressBar';
import { formatEta } from '../../utils/formatters';

interface HeaderProps {
  user: any;
  tracks: any[];
  activeCount: number;
  combinedNotifications: any[];
  connections: any[];
  syncingStrava: boolean;
  getFullVideoUrl: (path?: string | null) => string;
  setPlayingVideoUrl: (url: string | null) => void;
  handleLogout: () => Promise<void>;
  handleStravaSync: () => void;
  handleStravaConnect: () => void;
  handleDismissSync: (provider: string, e: React.MouseEvent) => void;
  openSettings: (tab: 'profile' | 'accounts' | 'activities' | 'billing') => void;
}

export const Header: React.FC<HeaderProps> = ({
  user,
  tracks,
  activeCount,
  combinedNotifications,
  connections,
  syncingStrava,
  getFullVideoUrl,
  setPlayingVideoUrl,
  handleLogout,
  handleStravaSync,
  handleStravaConnect,
  handleDismissSync,
  openSettings,
}) => {
  const navigate = useNavigate();
  const [showRendersDropdown, setShowRendersDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const rendersDropdownRef = useRef<HTMLDivElement>(null);
  const userDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (showRendersDropdown && rendersDropdownRef.current && !rendersDropdownRef.current.contains(e.target as Node)) {
        setShowRendersDropdown(false);
      }
      if (showUserDropdown && userDropdownRef.current && !userDropdownRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showRendersDropdown, showUserDropdown]);

  const getPlanBadgeColor = (plan?: string) => {
    switch (plan) {
      case 'ultra':
        return {
          background: 'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)',
          color: '#000',
          icon: <Zap size={14} style={{ fill: '#000' }} />,
        };
      case 'expert':
        return {
          background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
          color: '#fff',
          icon: <Award size={14} />,
        };
      case 'pro':
        return {
          background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
          color: '#fff',
          icon: <ShieldCheck size={14} />,
        };
      case 'starter':
        return {
          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
          color: '#fff',
          icon: <Award size={14} />,
        };
      default:
        return {
          background: 'rgba(255,255,255,0.08)',
          color: 'var(--text-secondary)',
          icon: <Award size={14} />,
        };
    }
  };

  const planBadgeStyle = getPlanBadgeColor(user?.plan);
  const isStravaLinked = connections.find((c) => c.provider === 'strava')?.linked;

  return (
    <header
      className="glass-panel"
      style={{
        borderRadius: 0,
        borderTop: 'none',
        borderInline: 'none',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(11, 15, 25, 0.85)',
        backdropFilter: 'blur(20px)',
      }}
    >
      <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
          onClick={() => navigate('/library')}
        >
          <span style={{ fontSize: '28px' }}>🗺️</span>
          <span
            style={{
              fontSize: '22px',
              fontWeight: 800,
              background: 'var(--accent-gradient)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            Trail Render
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Real-time Notification Bell */}
          <div ref={rendersDropdownRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowRendersDropdown(!showRendersDropdown)}
              className="btn btn-secondary"
              style={{
                padding: '10px',
                borderRadius: '50%',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Notifications"
            >
              <Bell size={18} style={{ color: activeCount > 0 ? '#ff7a00' : '#fff' }} />
              {activeCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: '#ff7a00',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 10px rgba(255,122,0,0.5)',
                    animation: 'pulse 1.8s infinite',
                  }}
                >
                  {activeCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showRendersDropdown && (
              <div
                className="glass-panel"
                style={{
                  position: 'absolute',
                  top: '50px',
                  right: 0,
                  width: '360px',
                  maxHeight: '450px',
                  overflowY: 'auto',
                  background: 'rgba(15, 23, 42, 0.95)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                  zIndex: 200,
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '14px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    paddingBottom: '8px',
                  }}
                >
                  <span style={{ fontWeight: 700, fontSize: '15px' }}>Notifications</span>
                  <button
                    onClick={() => setShowRendersDropdown(false)}
                    style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer' }}
                  >
                    <X size={16} />
                  </button>
                </div>

                {combinedNotifications.length === 0 ? (
                  <div style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                    No notifications.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {combinedNotifications.map((item) => {
                      if (item.type === 'render') {
                        const jobTrack = tracks.find((t) => t.id === item.trackId);
                        return (
                          <div
                            key={item.id}
                            onClick={() => {
                              navigate(`/status/${item.jobId}`);
                              setShowRendersDropdown(false);
                            }}
                            className="glass-panel-hover"
                            style={{
                              background: 'rgba(255,255,255,0.02)',
                              border: '1px solid rgba(255,255,255,0.04)',
                              borderRadius: '8px',
                              padding: '10px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                                <Film
                                  size={14}
                                  style={{
                                    color:
                                      item.status === 'completed'
                                        ? '#10b981'
                                        : item.status === 'failed'
                                          ? '#ef4444'
                                          : '#ff7a00',
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '160px',
                                  }}
                                >
                                  {jobTrack?.name || 'Unknown Track'}
                                </span>
                              </div>
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  textTransform: 'uppercase',
                                  background:
                                    item.status === 'completed'
                                      ? 'rgba(16,185,129,0.15)'
                                      : item.status === 'failed'
                                        ? 'rgba(239,68,68,0.15)'
                                        : 'rgba(245,158,11,0.15)',
                                  color:
                                    item.status === 'completed'
                                      ? '#10b981'
                                      : item.status === 'failed'
                                        ? '#ef4444'
                                        : '#f59e0b',
                                }}
                              >
                                {item.status === 'rendering' ? `rendering ${item.progress}%` : item.status}
                              </span>
                            </div>

                            <div
                              style={{
                                fontSize: '11px',
                                color: '#9ca3af',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span>
                                Resolution: {item.resolution} ({item.fps}fps)
                              </span>
                              {item.etaSeconds !== undefined && item.etaSeconds > 0 && (
                                <span>ETA: {formatEta(item.etaSeconds)}</span>
                              )}
                            </div>

                            {/* Progress bar */}
                            {(item.status === 'rendering' || item.status === 'queued') && (
                              <ProgressBar progress={item.progress} height={4} marginTop={4} />
                            )}

                            {item.status === 'completed' && item.videoUrl && (
                              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPlayingVideoUrl(item.videoUrl || null);
                                    setShowRendersDropdown(false);
                                  }}
                                  className="btn btn-primary"
                                  style={{ padding: '4px 8px', fontSize: '11px', borderRadius: '4px', flex: 1 }}
                                >
                                  <Play size={10} />
                                  <span>Watch</span>
                                </button>
                                <a
                                  href={getFullVideoUrl(item.videoUrl)}
                                  download
                                  onClick={(e) => e.stopPropagation()}
                                  className="btn btn-secondary"
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '11px',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Download size={10} />
                                </a>
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        // item.type === 'sync'
                        const providerTitle = item.provider.charAt(0).toUpperCase() + item.provider.slice(1);
                        return (
                          <div
                            key={item.id}
                            style={{
                              background: 'rgba(255,255,255,0.02)',
                              border: '1px solid rgba(255,255,255,0.04)',
                              borderRadius: '8px',
                              padding: '10px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '6px',
                              position: 'relative',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                                <Activity
                                  size={14}
                                  style={{
                                    color:
                                      item.status === 'completed'
                                        ? '#10b981'
                                        : item.status === 'failed'
                                          ? '#ef4444'
                                          : '#3b82f6',
                                    flexShrink: 0,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#fff',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '160px',
                                  }}
                                >
                                  {providerTitle} Sync
                                </span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase',
                                    background:
                                      item.status === 'completed'
                                        ? 'rgba(16,185,129,0.15)'
                                        : item.status === 'failed'
                                          ? 'rgba(239,68,68,0.15)'
                                          : item.status === 'paused'
                                            ? 'rgba(245,158,11,0.15)'
                                            : 'rgba(59,130,246,0.15)',
                                    color:
                                      item.status === 'completed'
                                        ? '#10b981'
                                        : item.status === 'failed'
                                          ? '#ef4444'
                                          : item.status === 'paused'
                                            ? '#f59e0b'
                                            : '#3b82f6',
                                  }}
                                >
                                  {item.status === 'syncing'
                                    ? `syncing ${item.progress}%`
                                    : item.status === 'fetching'
                                      ? 'fetching'
                                      : item.status === 'paused'
                                        ? `paused`
                                        : item.status}
                                </span>
                                {(item.status === 'completed' || item.status === 'failed') && (
                                  <button
                                    onClick={(e) => handleDismissSync(item.provider, e)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#9ca3af',
                                      cursor: 'pointer',
                                      padding: '2px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <X size={12} />
                                  </button>
                                )}
                              </div>
                            </div>

                            <div
                              style={{
                                fontSize: '11px',
                                color: '#9ca3af',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span
                                style={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  maxWidth: '240px',
                                }}
                                title={item.status === 'failed' ? item.error : undefined}
                              >
                                {item.status === 'fetching' && 'Fetching activities...'}
                                {item.status === 'syncing' && 'Importing activities...'}
                                {item.status === 'completed' && 'Synchronization completed'}
                                {item.status === 'failed' && `Failed: ${item.error || 'Unknown error'}`}
                              </span>
                              {item.etaSeconds !== null && item.etaSeconds !== undefined && item.etaSeconds > 0 && (
                                <span>ETA: {formatEta(item.etaSeconds)}</span>
                              )}
                            </div>

                            {/* Progress bar */}
                            {(item.status === 'syncing' || item.status === 'fetching') && (
                              <div
                                style={{
                                  width: '100%',
                                  background: 'rgba(255, 255, 255, 0.08)',
                                  borderRadius: '10px',
                                  height: '4px',
                                  marginTop: '4px',
                                  overflow: 'hidden',
                                }}
                              >
                                <div
                                  style={{
                                    height: '100%',
                                    background: '#3b82f6',
                                    width: `${item.progress}%`,
                                    transition: 'width 0.3s ease',
                                  }}
                                ></div>
                              </div>
                            )}
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Profile */}
          {user && (
            <div ref={userDropdownRef} style={{ position: 'relative' }}>
              <div
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: showUserDropdown ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                  padding: '4px 10px 4px 6px',
                  borderRadius: '30px',
                  border: '1px solid rgba(255,255,255,0.05)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'var(--accent-gradient)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 800,
                      fontSize: '12px',
                    }}
                  >
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
                    {user.name}
                  </span>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      fontSize: '9px',
                      fontWeight: 800,
                      color: planBadgeStyle.color,
                      textTransform: 'uppercase',
                    }}
                  >
                    {user.plan} plan
                  </div>
                </div>
                <ChevronDown
                  size={12}
                  style={{
                    color: 'var(--text-secondary)',
                    opacity: 0.7,
                    transform: showUserDropdown ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </div>

              {showUserDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: 0,
                    width: '200px',
                    background: 'rgba(15, 23, 42, 0.95)',
                    backdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '12px',
                    padding: '6px',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
                    zIndex: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <button
                    onClick={() => {
                      openSettings('profile');
                      setShowUserDropdown(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Settings size={14} style={{ color: 'var(--text-secondary)' }} />
                    <span>Settings</span>
                  </button>
                  <button
                    onClick={() => {
                      openSettings('accounts');
                      setShowUserDropdown(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Link size={14} style={{ color: 'var(--text-secondary)' }} />
                    <span>Linked Accounts</span>
                  </button>
                  <button
                    onClick={() => {
                      openSettings('activities');
                      setShowUserDropdown(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Activity size={14} style={{ color: 'var(--text-secondary)' }} />
                    <span>Activity Profiles</span>
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        setShowUserDropdown(false);
                        navigate('/admin');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        background: 'rgba(244, 63, 94, 0.1)',
                        border: '1px solid rgba(244, 63, 94, 0.2)',
                        borderRadius: '8px',
                        color: '#fb7185',
                        fontSize: '13px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'background 0.15s ease',
                      }}
                    >
                      <ShieldCheck size={14} />
                      <span>Admin Panel</span>
                    </button>
                  )}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', margin: '4px 6px' }} />
                  <button
                    onClick={() => {
                      setShowUserDropdown(false);
                      handleLogout();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#ef4444',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <LogOut size={14} />
                    <span>Log Out</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {isStravaLinked ? (
            <button
              onClick={() => handleStravaSync()}
              disabled={syncingStrava}
              className="btn btn-secondary"
              style={{
                padding: '8px 14px',
                fontSize: '13px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(252, 82, 0, 0.12)',
                border: '1px solid rgba(252, 82, 0, 0.25)',
                color: '#fc5200',
              }}
              title="Sync activities from Strava"
            >
              {syncingStrava ? (
                <div
                  className="spinner"
                  style={{
                    width: '12px',
                    height: '12px',
                    borderWidth: '1.5px',
                    borderColor: '#fc5200 transparent transparent transparent',
                  }}
                />
              ) : (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.6h3.065L11.214 0 5.343 11.745h3.063" />
                </svg>
              )}
              <span>{syncingStrava ? 'Syncing...' : 'Sync Strava'}</span>
            </button>
          ) : (
            <button
              onClick={handleStravaConnect}
              className="btn btn-secondary"
              style={{
                padding: '8px 14px',
                fontSize: '13px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                color: '#aaa',
              }}
              title="Link your Strava account"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.6h3.065L11.214 0 5.343 11.745h3.063" />
              </svg>
              <span>Connect Strava</span>
            </button>
          )}

          <button
            onClick={() => navigate('/upload')}
            className="btn btn-primary"
            style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '20px' }}
          >
            <Plus size={14} />
            <span>Upload</span>
          </button>
        </div>
      </div>
    </header>
  );
};
